import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { Referral } from '@/features/referrals/referrals-store';

/**
 * KPI derivation for the Dashboard home (SCRUM-326) — a faithful port of the
 * v6 prototype's `dashboardMetrics()` single pass over the customers:
 *
 *  - flight-active customers    → Deposits at Risk (Σ balance) + count
 *  - relgap-active customers    → "clear product matches" + opportunity deposits
 *  - lastCampaign ≤ 30 days ago → Customers Reached
 *  - Potential Revenue          = round(oppDeposits × 0.006 + relgapCount × 180)
 *    (prototype heuristic: a conservative revenue proxy — 0.6% of opportunity
 *    deposits plus a $180 per-match cross-sell value)
 *  - Product Conversions        = base + referral-driven conversions
 */
export interface DashboardMetrics {
  potentialRevenue: number;
  opportunities: number;
  flightDeposits: number;
  flightCount: number;
  reached30: number;
  productConversions: number;
  referralDriven: number;
}

/**
 * Prototype `DASH_DEMO.productConversionsBase`: non-referral product
 * acquisitions year-to-date. A demo constant until the ingestion pipeline
 * provides the real figure (SCRUM-341 conversions hook).
 */
export const PRODUCT_CONVERSIONS_BASE = 156;

export function dashboardMetrics(
  customers: readonly Customer[],
  referrals: readonly Referral[],
  /**
   * v7 branch scoping (SCRUM-388): Branch Manager / Teller dashboards compute
   * KPIs only from their branch's customers. Undefined = tenant-wide.
   */
  branchScope?: string,
): DashboardMetrics {
  let flightCount = 0;
  let flightDeposits = 0;
  let opportunities = 0;
  let oppDeposits = 0;
  let reached30 = 0;

  for (const c of customers) {
    if (branchScope !== undefined && c.branch !== branchScope) continue;
    if (c.insights.flight?.active) {
      flightCount++;
      flightDeposits += c.balance;
    }
    if (c.insights.relgap?.active) {
      opportunities++;
      oppDeposits += c.balance;
    }
    if (c.lastCampaign && c.lastCampaign.daysAgo <= 30) reached30++;
  }

  const referralDriven = referrals.filter((r) => r.converted).length;

  return {
    potentialRevenue: Math.round(oppDeposits * 0.006 + opportunities * 180),
    opportunities,
    flightDeposits,
    flightCount,
    reached30,
    referralDriven,
    productConversions: PRODUCT_CONVERSIONS_BASE + referralDriven,
  };
}

/** Prototype `dashFmtMoney`: $4.66M / $197K / $840 style compaction. */
export function fmtMoney(n: number): string {
  if (n >= 1e6) return `$${(n / 1e6).toFixed(n >= 1e7 ? 1 : 2)}M`;
  if (n >= 1e3) return `$${String(Math.round(n / 1e3))}K`;
  return `$${String(n)}`;
}
